import { MEDIA_UPLOAD_FIELD, type MediaAssetDto, type MediaAssetKind } from '@gc/shared';

import { announceIfVenueSuspended, ApiError, currentSession, freshAccessToken } from '@/lib/api';

/**
 * Multipart upload (SPEC §7).
 *
 * Kept out of `lib/api.ts` because that client always sets a JSON
 * `Content-Type`; a multipart body needs the browser to set it itself so the
 * boundary parameter is correct. Everything else — bearer token, cookie
 * credentials, error shape — matches the JSON client.
 *
 * NOTE: this does not attempt the JSON client's 401-refresh-and-replay. A
 * request body is a one-shot stream, so replaying it would need the file held
 * twice. It renews BEFORE sending instead: `freshAccessToken` swaps a token
 * that is due (or missing) for a new one first, so somebody who leaves a
 * settings drawer open, walks off and comes back to pick a photo is not told
 * the upload failed — the raw held token is exactly what can lapse in that
 * gap, and nothing here would ever have retried it.
 *
 * Its own 403 `venue_suspended` parks the app on the suspended screen, as
 * every JSON request's does (EclipseGG platform 2 review, 2026-10-20): it
 * went through no `request()`, so a fresh token's upload at a suspended venue
 * said only that the upload failed. Announced through `lib/api`, with the
 * session it was sent in, so one that outlived a sign-out parks nobody.
 */
export async function uploadMedia(file: File, kind: MediaAssetKind): Promise<MediaAssetDto> {
  const body = new FormData();
  body.append(MEDIA_UPLOAD_FIELD, file);

  const sentIn = currentSession();
  const token = await freshAccessToken();
  const response = await fetch(`/api/media?kind=${encodeURIComponent(kind)}`, {
    method: 'POST',
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body,
    credentials: 'include',
  });

  if (!response.ok) {
    let code = 'upload_failed';
    let message = 'Could not upload that file.';
    try {
      const payload = (await response.json()) as { code?: string; message?: string };
      if (payload.code) code = payload.code;
      if (payload.message) message = payload.message;
    } catch {
      // Non-JSON body (a proxy 413, say); keep the generic message.
    }
    const failure = new ApiError(response.status, code, message);
    announceIfVenueSuspended(failure, sentIn);
    throw failure;
  }

  return (await response.json()) as MediaAssetDto;
}
